/* ==========================================================================================
   APD 2026-08-22 — "New In" carousel behaviour: hold-to-drag, and wrap back to the start
   at the end. Owner review, this session.
   APD 2026-08-23 — the same wrap now also happens on a mobile SWIPE past the last card
   (owner request). See the "mobile: a swipe past the last card" block below.
   APD 2026-08-25 — the wrap is GONE and the row is ENDLESS instead. Owner: "endless slides,
   only to the right side". See the ENDLESS FORWARD block below; the old wind-back survives
   only as the fallback for a row too short to loop.

   WHY THIS FILE EXISTS INSTEAD OF A theme.js PATCH
   ------------------------------------------------
   theme.js's <scroll-carousel> already ships an `allow-drag` implementation, and the markup
   already sets the attribute — but it does not work on this page, and neither do the arrow
   enable/disable states nor the `is-scrollable` class. All three share one cause: every one
   of those listeners is registered inside `setupListeners()`, which theme.js calls from the
   ScrollCarousel *constructor* behind `if (this.allCells.length > 1)`. On top of that, the
   product cards contain <a> and <img>, which are natively draggable — a native drag gesture
   starts on mousedown+move and swallows the mousemove stream a drag-scroll needs.

   Rather than reach into the bundle, this re-implements the three behaviours on our own
   element. Two deliberate properties keep it safe if theme.js's own handler IS attached on
   some load path:

     - the drag sets scrollLeft as `startLeft - dx` (absolute), exactly as theme.js does, so
       two handlers running together compute the same value instead of doubling the speed;
     - the wrap intercept listens in the CAPTURE phase on the container, not the button, so
       it runs before the button's own click listener regardless of registration order.
   ========================================================================================== */

(() => {
  const DRAG_THRESHOLD = 3; // px before a press counts as a drag rather than a click
  const EDGE_TOLERANCE = 2; // px of slack when deciding "at the end"

  function init(carousel) {
    if (carousel.dataset.apdNewin) return;
    carousel.dataset.apdNewin = "1";

    const container = carousel.closest("product-list") || carousel.parentElement;
    const prevButton = container && container.querySelector(".prev-next-button--prev");
    const nextButton = container && container.querySelector(".prev-next-button--next");

    const maxScroll = () => carousel.scrollWidth - carousel.clientWidth;

    /* WHY THE WRAP KEPT FAILING (owner, three reports: "does not return to 1 photo when end
       reached"). The end of this row is NOT a scroll-snap point: the last page is short, so
       maxScroll lands mid-card. The tween suspends `scroll-snap-type` while it runs and restores it
       at the end — and the moment mandatory snapping comes back the browser pulls scrollLeft off
       maxScroll to the nearest card edge. The next press then read scrollLeft, concluded it was NOT
       at the end, paged forward to maxScroll again, got snapped back again, and so never wrapped.
       (It looked fine under automation only because requestAnimationFrame does not run there, so
       snapping never got a frame to act in — which is exactly why this needed the owner's eyes.)

       Fix: compare against where the last press AIMED, not where the browser left the row. Any real
       user interaction clears it, so dragging/wheeling still re-syncs to the truth. The row's
       snapping is also downgraded to `proximity` in section-newin.css so it stops fighting. */
    let commanded = null;
    const position = () => (commanded === null ? carousel.scrollLeft : commanded);
    const atEnd = () => position() >= maxScroll() - EDGE_TOLERANCE;
    /* Verified in the browser 2026-08-22: `carousel.scrollTo({behavior:"smooth"})` is a NO-OP on
       this element — scrollLeft never moves — while the instant form works. The scroll-snap
       container swallows the smooth animation. So the wrap is tweened by hand, with snapping
       switched off for the duration so it cannot fight the animation frame by frame. */
    /* Owner 2026-08-22, second round: "arrow scroll is still very rough, not smooth and slow as
       should be". Two causes, both fixed here.
         1. One flat 900ms for every distance. A page is ~one viewport (1029px here); the wrap back
            to the first card is 2744px — 2.7x the travel in the same time, so the wrap whipped.
            The duration now grows with sqrt(distance / viewport), so a page reads unhurried and the
            long rewind reads deliberate instead of snapping.
         2. Ease-OUT cubic starts at full speed, which is the part that reads as "rough" — the row
            jumps the instant the arrow is pressed. Ease-IN-OUT accelerates from rest and settles,
            which is what "smooth" means here.
       Base duration raised 900 -> 1300 because the owner asked for slower outright. */
    const DURATION = 1300;
    const MAX_DURATION = 2400; // so the full rewind still never crawls
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let tween = 0;
    let guard = 0;
    let animating = false;

    /* HOW THE ARROW MOTION IS DRIVEN (owner, four reports: "scroll is not smooth at all").

       Preferred path is the browser's OWN smooth scrolling — it runs on the compositor, so it stays
       smooth regardless of how much work the main thread is doing, which a per-frame JS tween cannot
       promise on a row of a dozen full-size product images. That is also the answer to "can you use
       bootstrap or any existing tool": Bootstrap has nothing for this, and the right existing tool is
       the platform's own scroller, not a library.

       But native smooth cannot simply be trusted here. A previous lane recorded it doing nothing on
       this element, and it is additionally disabled outright in some environments (headless/automation
       panes, and under an OS "reduce motion" setting). Re-tested this session and it did indeed move
       nothing — though in a pane where requestAnimationFrame is also dead, which cannot distinguish
       "no-op on this element" from "no-op in this harness". So rather than pick a side: issue the
       native scroll, then CHECK whether it actually started, and fall back to the JS tween if not.
       Whichever path runs, the row animates instead of teleporting. */
    const SMOOTH_PROBE_MS = 120; // long enough for a real smooth scroll to have visibly started

    const scrollTo = (left) => {
      cancelAnimationFrame(tween);
      clearTimeout(guard);
      const from = carousel.scrollLeft;
      const distance = left - from;
      if (!distance) return;

      if (reduceMotion.matches) {
        carousel.scrollLeft = left;
        return;
      }

      animating = true;

      // Try the platform first.
      carousel.style.scrollBehavior = "smooth";
      carousel.scrollLeft = left;

      guard = setTimeout(() => {
        if (Math.abs(carousel.scrollLeft - from) >= 1) {
          // Native smooth is doing the work; just clear the flag once it must have landed.
          guard = setTimeout(() => {
            carousel.style.removeProperty("scroll-behavior");
            animating = false;
            normalize();
            sync();
          }, 700);
          return;
        }
        // Native smooth did nothing — drive it ourselves.
        carousel.style.removeProperty("scroll-behavior");
        tweenTo(from, left);
      }, SMOOTH_PROBE_MS);
    };

    /* The fallback. Frame-by-frame, with snapping suspended so it cannot fight the animation, and a
       timer backstop because requestAnimationFrame does not fire in a tab that is not painting —
       without it the row would never land AND would be left with scroll-snap-type:none forever. */
    const tweenTo = (from, left) => {
      const distance = left - from;
      const span = Math.abs(distance) / Math.max(1, carousel.clientWidth);
      const duration = Math.min(DURATION * Math.max(1, Math.sqrt(span)), MAX_DURATION);

      let started;
      animating = true;
      carousel.style.setProperty("scroll-snap-type", "none");

      const finish = () => {
        cancelAnimationFrame(tween);
        clearTimeout(guard);
        carousel.scrollLeft = left;
        carousel.style.removeProperty("scroll-snap-type");
        animating = false;
        normalize();
        sync();
      };

      const step = (now) => {
        started ??= now;
        const t = Math.min((now - started) / duration, 1);
        carousel.scrollLeft = from + distance * easeInOutCubic(t);
        if (t < 1) tween = requestAnimationFrame(step);
        else finish();
      };

      tween = requestAnimationFrame(step);
      guard = setTimeout(finish, duration + 100);
    };

    /* --- native drag-and-drop is what blocks the gesture; refuse it ------------------- */
    carousel.addEventListener("dragstart", (event) => event.preventDefault());

    /* --- hold-to-drag ----------------------------------------------------------------- */
    let origin = null;
    let dragged = false;
    let suppressClick = false;

    ["wheel", "touchstart"].forEach((type) =>
      carousel.addEventListener(type, () => { commanded = null; }, { passive: true })
    );

    carousel.addEventListener("mousedown", (event) => {
      if (event.button !== 0) return;
      commanded = null;
      origin = { x: event.clientX, left: carousel.scrollLeft };
      dragged = false;
      suppressClick = false;
      carousel.style.setProperty("scroll-snap-type", "none");
    });

    document.addEventListener("mousemove", (event) => {
      if (!origin) return;
      const dx = event.clientX - origin.x;
      if (!dragged && Math.abs(dx) > DRAG_THRESHOLD) {
        dragged = true;
        carousel.classList.add("is-dragging");
      }
      if (!dragged) return;
      let target = origin.left - dx;
      /* Endless while the hand is down: the moment a whole lap has gone by, the viewport AND the
         gesture's own origin both slide back one lap, so the mapping from pointer to scroll stays
         1:1 and the drag can never reach the far end of the cloned content. */
      if (loopActive()) {
        while (target >= lap) {
          target -= lap;
          origin.left -= lap;
        }
        while (target < 0) {
          target += lap;
          origin.left += lap;
        }
      }
      carousel.scrollLeft = target;
      event.preventDefault();
    });

    document.addEventListener("mouseup", () => {
      if (!origin) return;
      origin = null;
      carousel.style.removeProperty("scroll-snap-type");
      carousel.classList.remove("is-dragging");
      if (!dragged) return;

      // A real drag must not also open the product that happened to be under the cursor.
      suppressClick = true;
      // ...and, only when the row is too short to loop, the old wind-back from the end.
      if (!loopActive() && atEnd()) scrollTo(0);
    });

    carousel.addEventListener(
      "click",
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    /* --- ENDLESS FORWARD ---------------------------------------------------------------
       Owner 2026-08-25: "endless slides, only to the right side."

       Until today the row ENDED. Reaching the last card, the next press tweened all the way back
       to scrollLeft 0 — a 2744px LEFTWARD rewind, i.e. the one direction the reader is not going.
       Now the strip loops: the card set is CLONED into the same scroller until the content is at
       least one lap plus two viewports wide, so there is always more row to the right, and once a
       whole lap has scrolled by the position is moved back by exactly one lap. The pixels either
       side of that jump are identical — the clone is the same cards in the same order at the same
       pitch — so nothing is visible. The row simply never runs out.

       Why clones and not rotating the first card to the end: moving a <product-card> in the DOM
       runs its disconnected/connected callbacks and drops it out of and back into the lazy-image
       observer, once per card per lap, mid-gesture. Cloning touches the DOM once.

       A lap is MEASURED, not computed — the x distance from the first card to its own clone. That
       is the sum of every pitch including the grid's column gap, whatever the breakpoint does to
       card width, and it is an exact whole number of pitches, so the jump also lands on a
       scroll-snap point and `scroll-snap-type: x proximity` cannot pull it off afterwards.
       ------------------------------------------------------------------------------------ */
    const LOOP_SLACK = 2; // viewports of content that must exist beyond one lap
    const LOOP_MAX_SETS = 8; // backstop: three cards on a wide screen would clone for ever
    const originals = Array.from(carousel.children);
    let clonesAdded = 0;
    let lap = 0;

    const loopActive = () => lap > 0;

    /* ⚠ A CLONED PRODUCT CARD MUST BE RE-KEYED BEFORE IT ENTERS THE DOM, or <variant-picker> throws
       on every copy. Measured: 20 clones -> 20 uncaught TypeErrors, "Cannot read properties of null
       (reading 'value')" out of theme.js's getSelectedOptionValues, which does

           optionSelector.querySelector('input[name^="option"]:checked, ...').value

       and finds nothing checked. The cause is not the clone's markup — it is that a radio group is
       keyed by (form owner, name). The theme's quick-add markup keeps the option radios OUTSIDE the
       <form> and ties them to it with `form="<id>"`, so once a copy carries the same ids and the same
       `option1-<variant id>` names, the browser resolves that duplicated id to the FIRST form in the
       document and the copy's radios join the ORIGINAL card's group. One group, one checked radio, so
       every other card in that group reports nothing selected.

       So each set gets its own suffix on every id, on every attribute that points at an id, and on
       every radio/checkbox name. Names of anything else are left alone on purpose — the quick-add
       form submits `id` and `quantity`, and renaming those would break add-to-cart on a clone. */
    const ID_REFS = ["for", "form", "list", "aria-controls", "aria-labelledby", "aria-describedby", "aria-owns", "popovertarget"];

    const rekey = (root, suffix) => {
      const renamed = new Map();

      root.querySelectorAll("[id]").forEach((node) => {
        renamed.set(node.id, node.id + suffix);
        node.id += suffix;
      });

      root.querySelectorAll("*").forEach((node) => {
        ID_REFS.forEach((attribute) => {
          const value = node.getAttribute(attribute);

          if (!value) return;

          const mapped = value
            .split(/\s+/)
            .map((token) => renamed.get(token) || token)
            .join(" ");

          if (mapped !== value) node.setAttribute(attribute, mapped);
        });
      });

      root.querySelectorAll('input[type="radio"][name], input[type="checkbox"][name]').forEach((node) => {
        node.name += suffix;
      });
    };

    const cloneSet = () => {
      const fragment = document.createDocumentFragment();
      const suffix = "--apd-loop-" + (clonesAdded + 1);

      originals.forEach((cell) => {
        const copy = cell.cloneNode(true);

        copy.dataset.apdLoopClone = "1";
        rekey(copy, suffix);
        /* A screen reader must not read the same twelve products twice over, and Tab must not walk
           them either — but a mouse click on a clone still has to open the product, so the copies
           stay clickable rather than inert. */
        copy.setAttribute("aria-hidden", "true");
        copy.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach((node) => {
          node.setAttribute("tabindex", "-1");
        });
        fragment.appendChild(copy);
      });

      carousel.appendChild(fragment);
      clonesAdded += 1;
    };

    const measureLap = () => {
      const firstClone = carousel.querySelector('[data-apd-loop-clone="1"]');

      if (!originals.length || !firstClone) return 0;

      return firstClone.offsetLeft - originals[0].offsetLeft;
    };

    /* Runs at init and on every resize: --product-list-card-width is responsive, so a lap is not
       a constant and neither is how many sets it takes to cover the viewport. */
    const ensureLoop = () => {
      const view = carousel.clientWidth;

      if (originals.length < 2 || !view) return;

      if (!clonesAdded) {
        // A row that already fits has nothing to loop, and needed no wrap either.
        if (carousel.scrollWidth <= view + 1) return;
        cloneSet();
      }

      lap = measureLap();

      if (!lap) return;

      while (carousel.scrollWidth - view < lap + LOOP_SLACK * view && clonesAdded < LOOP_MAX_SETS) {
        cloneSet();
      }
    };

    /* Move the viewport a whole number of laps with no animation. Same pixels, so invisible —
       but snapping is suspended across it so it cannot land on a neighbouring card edge, and
       both the commanded target and any drag in flight move with it or they would disagree with
       what is on screen. */
    const shiftBy = (delta) => {
      if (!delta) return;

      const snap = carousel.style.scrollSnapType;

      carousel.style.setProperty("scroll-snap-type", "none");
      carousel.scrollLeft += delta;

      if (snap) carousel.style.setProperty("scroll-snap-type", snap);
      else carousel.style.removeProperty("scroll-snap-type");

      if (commanded !== null) commanded += delta;
      if (origin) origin.left += delta;
    };

    const normalize = () => {
      if (!loopActive()) return;

      const laps = Math.floor(carousel.scrollLeft / lap);

      if (laps > 0) shiftBy(-laps * lap);
    };

    /* Native scrolling — a trackpad flick, a touch swipe — never goes through scrollTo, so it is
       normalised once it has come to rest. 180ms of quiet is the settle test rather than the
       `scrollend` event, which Safari only shipped in 18.2. Normalising mid-momentum would stop
       the momentum, which is why this waits. */
    let settleTimer = 0;

    carousel.addEventListener(
      "scroll",
      () => {
        if (!loopActive()) return;
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          if (animating || origin) return;
          commanded = null;
          normalize();
        }, 180);
      },
      { passive: true }
    );

    /* --- the arrows -------------------------------------------------------------------
       We intercept EVERY arrow click, not just the ones at the ends. theme.js's own
       next()/previous() land on `scrollTo({behavior:"smooth"})`, which is the call verified
       to do nothing on this element — so out of the box the arrows move the row not at all,
       at any position. Listening in the CAPTURE phase on the container means this runs
       before the button's own handler whatever order they registered in, and
       stopPropagation keeps theme.js's dead scroll out of the way.

       A "page" is a whole number of cards — never a part card — so the row always comes to
       rest on a card edge and agrees with CSS scroll-snap. */
    const page = () => {
      const cells = carousel.querySelectorAll("product-card");
      if (cells.length < 2) return carousel.clientWidth;
      const pitch = cells[1].getBoundingClientRect().left - cells[0].getBoundingClientRect().left;
      if (pitch <= 0) return carousel.clientWidth;
      return Math.max(1, Math.floor(carousel.clientWidth / pitch)) * pitch;
    };

    const atStart = () => position() <= EDGE_TOLERANCE;

    const advance = (isNext) => {
      const step = page();

      if (loopActive()) {
        /* Forward is now always forward: one page to the RIGHT, for ever, with no rewind at any
           point. Backward still works — if there is less than a page of room to the left, the
           viewport jumps one lap forward first (invisible, same pixels) and then travels left from
           there, so the row loops that way too instead of leaping to the far end as it used to. */
        if (!isNext && position() - step < 0) shiftBy(lap);

        const target = position() + (isNext ? step : -step);

        commanded = target;
        scrollTo(target);
        return;
      }

      const max = maxScroll();
      const target = isNext
        ? (atEnd() ? 0 : Math.min(position() + step, max))            // past the last card -> first
        : (atStart() ? max : Math.max(position() - step, 0));
      commanded = target;
      scrollTo(target);
    };

    /* --- mobile: a swipe past the last card winds back to the first --------------------
       Owner 2026-08-23: "should swiping come back when reach end in product cards below hero in
       mobile version? now it does not. you can implement, it is convenient."

       On the phone there are no arrows, so the row is a native overflow scroll and it simply stops
       at the end. This gives the swipe the same wrap the arrows already have.

       How it decides, without fighting the native scroll: on touchstart it records scrollLeft and
       whether the row was already AT the end. On touchend it wraps only if all four hold —
         * one finger,
         * the gesture was mostly horizontal (|dx| > |dy|), so a vertical page scroll never triggers it,
         * it travelled more than SWIPE_THRESHOLD, so a tap or a twitch never does either,
         * and scrollLeft did NOT move, i.e. the row was pinned at the end and the swipe had nowhere
           to go. That last test is what keeps this out of the way of every ordinary swipe.
       The wrap reuses the same hand-written tween the arrows use (scrollTo), so it eases back rather
       than jumping — and `scrollTo` already handles the snap-fighting described above.

       FORWARD ONLY, deliberately. The arrows wrap both ways because a button press is unambiguous; a
       swipe is not, and jumping to the END because someone flicked right on the first card would be a
       surprise. Flagged to the designer rather than assumed. */
    const SWIPE_THRESHOLD = 40; // px of horizontal travel before a swipe counts as one

    let touchOrigin = null;

    carousel.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length !== 1) {
          touchOrigin = null;
          return;
        }
        const point = event.touches[0];
        touchOrigin = {
          x: point.clientX,
          y: point.clientY,
          left: carousel.scrollLeft,
          wasAtEnd: carousel.scrollLeft >= maxScroll() - EDGE_TOLERANCE,
        };
      },
      { passive: true }
    );

    carousel.addEventListener(
      "touchend",
      (event) => {
        const start = touchOrigin;
        touchOrigin = null;
        /* Nothing to wrap once the row loops — a swipe just keeps going into the clones and the
           settle handler above quietly puts the position back inside the first lap. */
        if (loopActive()) return;
        if (!start || !start.wasAtEnd || animating) return;

        const point = event.changedTouches && event.changedTouches[0];
        if (!point) return;

        const dx = point.clientX - start.x;
        const dy = point.clientY - start.y;
        if (dx > -SWIPE_THRESHOLD) return;                       // not a leftward swipe of any size
        if (Math.abs(dx) <= Math.abs(dy)) return;                // more vertical than horizontal
        if (Math.abs(carousel.scrollLeft - start.left) > EDGE_TOLERANCE) return; // it scrolled: leave it

        commanded = null;
        scrollTo(0);
      },
      { passive: true }
    );

    if (container) {
      /* Act on MOUSEDOWN, not click: the owner wants the row to move on the press, and a press
         is also the only event we can trust here. `click` is dispatched on the nearest common
         ancestor of the mousedown and mouseup targets — so anything that moves the icon under
         the cursor mid-press (the theme's hover animation used to) silently retargets the click
         above the button and the arrow does nothing. mousedown has no such ambiguity. */
      container.addEventListener(
        "mousedown",
        (event) => {
          if (event.button !== 0) return;
          const button = event.target.closest(".prev-next-button");
          if (!button || !container.contains(button)) return;
          event.preventDefault();
          event.stopPropagation();
          advance(button.classList.contains("prev-next-button--next"));
        },
        true
      );

      // ...and swallow the click that follows, so theme.js's own (dead) handler cannot also run
      // and so a press never counts twice.
      container.addEventListener(
        "click",
        (event) => {
          const button = event.target.closest(".prev-next-button");
          if (!button || !container.contains(button)) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      // Keyboard and assistive tech never send mousedown — keep the buttons operable.
      container.addEventListener("keydown", (event) => {
        const button = event.target.closest && event.target.closest(".prev-next-button");
        if (!button || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        advance(button.classList.contains("prev-next-button--next"));
      });
    }

    /* --- state theme.js never gets round to setting ------------------------------------ */
    const sync = () => {
      /* This runs on every scroll event, and the tween fires one per frame. Reading scrollWidth /
         clientWidth here forces a synchronous layout, so during an animation it was doing that ~60
         times a second on a row of twelve full-size product cards — a real contributor to the
         "very rough" motion the owner reported. Nothing it sets can change mid-tween anyway. */
      if (animating) return;
      carousel.classList.toggle("is-scrollable", carousel.scrollWidth > carousel.clientWidth);
      /* Owner 2026-08-26: "hide < > if there is nothing to scroll". The row only overflows when the
         collection holds more cards than fit, so with a three-product drop on a wide screen the
         arrows were sitting there doing nothing on every press.

         The test is `loopActive()` OR real overflow, not overflow alone: once the endless loop has
         cloned the card set, scrollWidth is always far wider than the viewport, so overflow alone
         would answer "scrollable" even for a row that only ever fit. loopActive() is the honest
         record of whether ensureLoop() found something to loop in the first place.

         The class is set on the container the arrows live in, and it HIDES rather than shows — so
         if this script never runs the arrows stay exactly as they are today rather than vanishing. */
      if (container) {
        const scrollable = loopActive() || carousel.scrollWidth > carousel.clientWidth + 1;
        container.classList.toggle("apd-nothing-to-scroll", !scrollable);
      }
      // Both arrows stay live because the carousel wraps; the markup ships prev as disabled,
      // and nothing in theme.js ever clears it on this page.
      if (prevButton) prevButton.disabled = false;
      if (nextButton) nextButton.disabled = false;
    };

    carousel.addEventListener("scroll", sync, { passive: true });
    /* Card width is responsive, so a resize can change the lap AND how many clone sets it takes to
       keep a viewport of row to the right of any position. Re-measure, re-fill, then put the
       position back inside the first lap in case the lap just got shorter. */
    new ResizeObserver(() => {
      ensureLoop();
      normalize();
      sync();
    }).observe(carousel);
    ensureLoop();
    sync();
  }

  const boot = () => document.querySelectorAll('[id$="__new_in_carousel"] scroll-carousel').forEach(init);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Theme editor re-renders the section on every settings change.
  document.addEventListener("shopify:section:load", boot);
})();
